import { organizationUserCategoryIds, userOrganizationSelections, organizationTaskSelectionIds } from "../../utils/organizationTaskCategories";
import DataTable from "../../components/DataTable";
import OrganizationTaskAssignments from "../../components/OrganizationTaskAssignments";
import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import Select from "react-select";
import Pagination from "../../components/Pagination";
import { Modal, Button } from "react-bootstrap";
import useAccess from "../../hooks/useAccess";
import StyledSelect from "../../components/StyledSelect";
import "../../styles/AssignTasksPage.css";

const parseJsonSafe = (raw) => {
  try {
    if (raw == null) return [];
    if (Array.isArray(raw)) return raw;
    if (typeof raw === "string") return JSON.parse(raw);
    if (typeof raw === "number") return [raw];
    if (typeof raw === "object")
      return Array.isArray(raw) ? raw : Object.values(raw);
    return [];
  } catch {
    return [];
  }
};

const TASK_PROGRAM_SYNC_ALLOWED_EMAILS = new Set([
  "daniel.chow@newwaytrucking.com",
]);

const getTokenEmail = (token) => {
  try {
    const encoded = String(token || "").split(".")[1] || "";
    const normalized = encoded.replace(/-/g, "+").replace(/_/g, "/");
    const payload = JSON.parse(window.atob(normalized));
    return String(payload?.email || "").trim().toLowerCase();
  } catch {
    return "";
  }
};

const getCurrentUserEmail = (token) => {
  try {
    const me = JSON.parse(localStorage.getItem("me") || "{}");
    if (me?.email) return String(me.email).trim().toLowerCase();
  } catch {
    // Fall back to token parsing for older sessions.
  }
  return getTokenEmail(token);
};

const AssignTasksPage = ({ userId }) => {
  const BASE_URL = process.env.REACT_APP_API_BASE_URL;
  const token = localStorage.getItem("token");
  const headers = useMemo(
    () => ({ Authorization: `Bearer ${token}` }),
    [token]
  );

  // Data
  const [categories, setCategories] = useState([]);
  const [allTasks, setAllTasks] = useState([]); // used globally
  const [modalTasks, setModalTasks] = useState([]); // used in edit modal
  const [programs, setPrograms] = useState([]);
  const [assignmentRefreshKey, setAssignmentRefreshKey] = useState(0);
  const [selectedOrgIds, setSelectedOrgIds] = useState([]);
  const [manualOrgIds, setManualOrgIds] = useState([]);
  const [userCategoryIds, setUserCategoryIds] = useState([]);
  const [orgSelectionChanged, setOrgSelectionChanged] = useState(false);
  const [orgManagedTaskIds, setOrgManagedTaskIds] = useState([]);
  const [manualTaskIds, setManualTaskIds] = useState([]);

  // Selections
  const [selectedTaskIds, setSelectedTaskIds] = useState([]);
  const [selectedProgramIds, setSelectedProgramIds] = useState([]);
  const [programManagedTaskIds, setProgramManagedTaskIds] = useState([]);
  const [isSaving, setIsSaving] = useState(false);

  // Edit modal
  const [editingProgram, setEditingProgram] = useState(null);
  const [editingTaskIds, setEditingTaskIds] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [positionCategories, setPositionCategories] = useState([]);
  const [editingCategoryIds, setEditingCategoryIds] = useState([]);

  // Filters (bottom table)
  const [filterCategory, setFilterCategory] = useState("");
  const [q, setQ] = useState("");
  const [programQuery, setProgramQuery] = useState("");

  // Pagination (match AssignTrainingPage style)
  const [page, setPage] = useState(1);
  const perPage = 12;
  const programPerPage = 8;
  const [isSyncingPrograms, setIsSyncingPrograms] = useState(false);
  const [syncJob, setSyncJob] = useState(null);
  const [isCancelingSync, setIsCancelingSync] = useState(false);
  const syncPollTimerRef = useRef(null);

  // 🧪 State: Access & Auth
  const { hasPermission } = useAccess();

  // ⭐ NEW: Centralized PERMISSION Map
  const PERMISSIONS = {
    tasks: {
      assign: hasPermission("Task", "Task Assignment", "edit"),
      edit: hasPermission("Task", "Task Assignment", "edit"),
    },
  };
  const canRunTaskProgramSync = TASK_PROGRAM_SYNC_ALLOWED_EMAILS.has(
    getCurrentUserEmail(token)
  );

  // Load initial data
  useEffect(() => {
    const load = async () => {
      const [cats, tasks, progs, assigned, user] = await Promise.all([
        axios.get(`${BASE_URL}/api/tasks/categories`, { headers }),
        axios.get(`${BASE_URL}/api/tasks`, { headers }),
        axios.get(`${BASE_URL}/api/tasks/programs`, { headers }),
        axios.get(`${BASE_URL}/api/tasks/assigned/${userId}?includeHidden=1`, {
          headers,
        }),
        axios.get(`${BASE_URL}/api/users/${userId}`, { headers }),
      ]);

      setOrgSelectionChanged(false);
      setUserCategoryIds(organizationUserCategoryIds(user.data?.emp_category));
      setCategories(cats.data || []);
      setAllTasks(tasks.data || []);
      setPrograms(progs.data || []);
      const orgSelection = userOrganizationSelections(user.data?.task_orgs,user.data?.org_unit);
      setSelectedOrgIds(orgSelection.orgIds);
      setManualOrgIds(orgSelection.manualOrgIds);
      setOrgManagedTaskIds((assigned.data?.tasks || []).filter(t => t.uat_source === "organization" && Number(t.uat_visible ?? 1) === 1).map(t => Number(t.t_id)));

      // Assigned tasks -> start state
      const assignedTaskIds = (assigned.data?.tasks || [])
        .filter((t) => Number(t.uat_visible ?? 1) === 1)
        .map((t) => Number(t.t_id));
      setSelectedTaskIds(assignedTaskIds);
      setManualTaskIds((assigned.data?.tasks || []).filter(t => t.uat_source === "manual" && Number(t.uat_visible ?? 1) === 1).map(t => Number(t.t_id)));
      setProgramManagedTaskIds(
        (assigned.data?.tasks || [])
          .filter((t) => t.uat_source === "program")
          .map((t) => Number(t.t_id))
          .filter(Number.isFinite)
      );

      // Stored program selection on user (JSON)
      const userPrograms = parseJsonSafe(user.data?.task_programs || []);
      setSelectedProgramIds(
        userPrograms.map(Number).filter(Number.isFinite)
      );
    };

    load().catch(console.error);
  }, [BASE_URL, headers, userId, assignmentRefreshKey]);

  // A program definition is applied only when an administrator toggles that
  // program here. Editing a program never silently changes an existing user.
  const toggleProgram = (progId) => {
    const isOn = selectedProgramIds.includes(progId);
    const nextProgramIds = isOn
      ? selectedProgramIds.filter((id) => id !== progId)
      : [...selectedProgramIds, progId];
    const nextProgramTaskIds = Array.from(
      new Set(
        programs
          .filter((program) => nextProgramIds.includes(program.id))
          .flatMap((program) => program.taskIds)
          .map(Number)
          .filter(Number.isFinite)
      )
    );

    setSelectedProgramIds(nextProgramIds);
    setSelectedTaskIds(previous => [...new Set([
      ...previous.filter(id => !programManagedTaskIds.includes(id)),
      ...manualTaskIds,
      ...orgManagedTaskIds,
      ...nextProgramTaskIds,
    ])]);
    setProgramManagedTaskIds(nextProgramTaskIds);
  };

  const toggleOrganization = (orgId, orgs, checked) => {
    const adding = checked ?? !selectedOrgIds.includes(orgId);
    const nextOrgIds = adding ? [...new Set([...selectedOrgIds,orgId])] : selectedOrgIds.filter(id=>id!==orgId);
    const nextManualIds = adding ? [...new Set([...manualOrgIds,orgId])] : manualOrgIds.filter(id=>id!==orgId);
    const nextOrgTaskIds = organizationTaskSelectionIds(orgs,nextOrgIds,nextManualIds,userCategoryIds,allTasks);
    setManualOrgIds(nextManualIds);
    const retainedProgramTaskIds = programs.filter(program => selectedProgramIds.includes(program.id)).flatMap(program => program.taskIds).map(Number);
    setOrgSelectionChanged(true);
    setSelectedOrgIds(nextOrgIds);
    setSelectedTaskIds(previous => [...new Set([
      ...previous.filter(id => !orgManagedTaskIds.includes(id)),
      ...retainedProgramTaskIds,
      ...manualTaskIds,
      ...nextOrgTaskIds,
    ])]);
    setOrgManagedTaskIds(nextOrgTaskIds);
  };

  // Toggle an individual task (bottom table)
  const toggleTask = (id) => {
    const removing = selectedTaskIds.includes(id);
    setSelectedTaskIds(previous => removing ? previous.filter(taskId => taskId !== id) : [...previous, id]);
    setManualTaskIds(previous => removing ? previous.filter(taskId => taskId !== id) : [...new Set([...previous, id])]);
  };

  // Open edit modal for a program
  const openEditModal = async (program) => {
    setEditingProgram(program);
    setEditingTaskIds(program.taskIds);
    setEditingCategoryIds(program.rawCategory || []);

    try {
      const [tasksRes, catsRes] = await Promise.all([
        axios.get(`${BASE_URL}/api/tasks/editlist`, {
          headers,
          params: { programName: program.name },
        }),
        axios.get(`${BASE_URL}/api/tasks/positioncategories`, { headers }),
      ]);
      setModalTasks(tasksRes.data || []);
      setPositionCategories(catsRes.data || []);
    } catch (err) {
      console.error("❌ Failed to load edit modal data", err);
      setModalTasks([]);
      setPositionCategories([]);
    }

    setShowModal(true);
  };

  // Save edited tasks for a program
  const saveEditedTasks = async () => {
    if (!editingProgram) return;

    try {
      await axios.put(
        `${BASE_URL}/api/tasks/programs/${editingProgram.id}`,
        { taskIds: editingTaskIds, categoryIds: editingCategoryIds },
        { headers }
      );

      // 🧠 Find category names to display (convert IDs to labels)
      const updatedCategoryNames = positionCategories
        .filter((c) => editingCategoryIds.includes(c.c_id))
        .map((c) => c.c_name);

      // 🧩 Update frontend immediately (no refresh needed)
      setPrograms((prev) =>
        prev.map((p) =>
          p.id === editingProgram.id
            ? {
                ...p,
                taskIds: editingTaskIds,
                category: updatedCategoryNames,
                rawCategory: editingCategoryIds,
              }
            : p
        )
      );

      setShowModal(false);
      alert("✔️ Program tasks & categories updated!");
    } catch (err) {
      console.error("❌ Failed to save edited tasks", err);
      alert("⚠️ Failed to save changes. Please try again.");
    }
  };

  // One transaction saves the user's explicit program and task selections.
  const handleSave = async () => {
    await axios.post(
      `${BASE_URL}/api/tasks/assign`,
      { userId, taskIds: selectedTaskIds, programIds: selectedProgramIds, ...(orgSelectionChanged ? { orgIds: selectedOrgIds, manualOrgIds } : {}) },
      { headers }
    );

    alert("✔️ Saved task programs & assignments.");
  };
  const handleSaveWithState = async () => {
    if (isSaving) return;

    try {
      setIsSaving(true);
      await handleSave();
    } catch (err) {
      console.error("Failed to save task assignments", err);
      alert("Failed to save task assignments. Please try again.");
    } finally {
      setIsSaving(false);
    }
  };

  const handleSyncExistingUsers = async () => {
    if (isSyncingPrograms) return;

    try {
      setIsSyncingPrograms(true);
      setIsCancelingSync(false);
      setSyncJob({
        progress: 1,
        message: "Starting task program sync...",
        status: "queued",
      });

      const res = await axios.post(
        `${BASE_URL}/api/tasks/programs/sync-all`,
        {},
        { headers }
      );

      const jobId = res.data?.jobId;
      if (!jobId) {
        throw new Error("Sync job was not created.");
      }

      setSyncJob(res.data?.job || { id: jobId, progress: 1, status: "queued" });
      pollSyncJob(jobId);
    } catch (err) {
      console.error("Failed to sync existing task programs", err);
      alert(
        err?.response?.data?.message ||
          "Failed to sync existing task programs. Please try again."
      );
      setIsSyncingPrograms(false);
      setSyncJob(null);
    }
  };

  const pollSyncJob = (jobId) => {
    if (syncPollTimerRef.current) {
      clearTimeout(syncPollTimerRef.current);
    }

    syncPollTimerRef.current = setTimeout(async () => {
      try {
        const res = await axios.get(
          `${BASE_URL}/api/tasks/programs/sync-all/${jobId}`,
          { headers }
        );
        const nextJob = res.data?.job;
        setSyncJob(nextJob);

        if (["queued", "running", "canceling"].includes(nextJob?.status)) {
          pollSyncJob(jobId);
          return;
        }

        setIsSyncingPrograms(false);
        setIsCancelingSync(false);

        if (nextJob?.status === "complete") {
          setAssignmentRefreshKey(key => key + 1);
          alert(`Sync complete. Updated ${Number(nextJob.syncedUsers || 0)} users.`);
        } else if (nextJob?.status === "failed") {
          alert(nextJob.error || "Sync failed.");
        }
      } catch (err) {
        console.error("Failed to poll task program sync", err);
        setIsSyncingPrograms(false);
        setIsCancelingSync(false);
        alert("Could not check sync progress. Please refresh and try again.");
      }
    }, 1000);
  };

  const handleCancelSync = async () => {
    if (!syncJob?.id || isCancelingSync) return;

    try {
      setIsCancelingSync(true);
      const res = await axios.post(
        `${BASE_URL}/api/tasks/programs/sync-all/${syncJob.id}/cancel`,
        {},
        { headers }
      );
      setSyncJob(res.data?.job || syncJob);
    } catch (err) {
      console.error("Failed to cancel task program sync", err);
      alert("Failed to cancel sync. It may already be finishing.");
      setIsCancelingSync(false);
    }
  };

  const filteredPrograms = useMemo(() => {
    const term = programQuery.trim().toLowerCase();
    if (!term) return programs;

    return programs.filter((program) => {
      const name = String(program.name || "").toLowerCase();
      const categories = Array.isArray(program.category)
        ? program.category.join(", ").toLowerCase()
        : "";
      return name.includes(term) || categories.includes(term);
    });
  }, [programs, programQuery]);

  // Filtering + paging
  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    return allTasks.filter((t) => {
      const catOK =
        !filterCategory || String(t.t_category_id) === String(filterCategory);
      if (!term) return catOK;
      const title = String(t.t_title || "").toLowerCase();
      const catName = String(t.category_name || "").toLowerCase();
      return catOK && (title.includes(term) || catName.includes(term));
    });
  }, [allTasks, q, filterCategory]);

  const paged = useMemo(() => {
    const start = (page - 1) * perPage;
    return filtered.slice(start, start + perPage);
  }, [filtered, page]);

  useEffect(() => setPage(1), [q, filterCategory]);
  useEffect(
    () => () => {
      if (syncPollTimerRef.current) {
        clearTimeout(syncPollTimerRef.current);
      }
    },
    []
  );

  // UI helpers
  // const taskTitleById = useMemo(() => {
  //   const m = new Map();
  //   allTasks.forEach((t) => m.set(t.t_id, t.t_title));
  //   return m;
  // }, [allTasks]);

  return (
    <div className="container py-3 assign-tasks-page">
      {/* Page Header */}
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h3 className="m-0 text-center flex-grow-1">Assign Tasks</h3>
      </div>

      {/* Programs (top) */}
      <div className="assign-tasks-section-header mt-3 mb-2">
        <h5 className="m-0">Program Assignment List</h5>
        <div className="nw-filter-bar assign-tasks-filters assign-tasks-program-filters">
          <input
            className="form-control"
            placeholder="Search program or category"
            value={programQuery}
            onChange={(e) => setProgramQuery(e.target.value)}
          />
          {canRunTaskProgramSync && (
            <button
              className="btn btn-outline-primary"
              onClick={handleSyncExistingUsers}
              disabled={isSyncingPrograms}
            >
              {isSyncingPrograms ? "Syncing..." : "Sync"}
            </button>
          )}
        </div>
      </div>
      <DataTable
        disableEnhancement
        data={filteredPrograms}
        itemsPerPage={programPerPage}
        wrapperClassName="mb-4"
        tableClassName="assign-tasks-definition-table"
        emptyMessage="No task programs match your search."
        columns={[
          { key: "name", label: "Program Name", width: "34%" },
          { key: "category", label: "Employee Category", getValue: p => Array.isArray(p.category) ? p.category.join(", ") : "" },
          { key: "edit", label: "Edit Program Tasks", width: 150, sortable: false, headerClassName: "text-center assignment-action-column", cellClassName: "text-center assignment-action-column",
            render: p => <div className="assignment-action-group"><button type="button" className="app-table-action-button is-edit" onClick={() => openEditModal(p)} disabled={!PERMISSIONS.tasks.edit} title="Edit program tasks" aria-label={`Edit tasks for ${p.name}`}><i className="fas fa-edit" aria-hidden="true" /></button></div> },
          { key: "assigned", label: "Assigned", width: 120, headerClassName: "text-center", cellClassName: "text-center", getSortValue: p => selectedProgramIds.includes(p.id),
            render: p => <input type="checkbox" checked={selectedProgramIds.includes(p.id)} onChange={() => toggleProgram(p.id)} disabled={!PERMISSIONS.tasks.assign} aria-label={`Assign ${p.name}`} /> },
        ]}
      />

      <OrganizationTaskAssignments tasks={allTasks} canEdit={PERMISSIONS.tasks.edit} selectedOrgIds={selectedOrgIds} onToggle={toggleOrganization} assigning={isSaving} onSyncComplete={() => setAssignmentRefreshKey(key => key + 1)} />

      {/* Tasks (bottom) */}
      <div className="assign-tasks-section-header mb-2">
        <h5 className="m-0">User Task Assignment</h5>
        <div className="nw-filter-bar assign-tasks-filters assign-tasks-user-filters">
          <StyledSelect
            value={filterCategory}
            onChange={setFilterCategory}
            options={[
              { value: "", label: "All Categories" },
              ...categories.map((category) => ({
                value: category.tc_id,
                label: category.tc_name,
              })),
            ]}
            minWidth={180}
            ariaLabel="Filter tasks by category"
          />
          <input
            className="form-control"
            placeholder="Search task or category"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
        </div>
      </div>

      <div className="table-responsive">
        <table className="table table-bordered">
          <thead className="table-light">
            <tr>
              <th>Task</th>
              <th>Category</th>
              <th className="text-center" style={{ width: 120 }}>
                Assigned
              </th>
            </tr>
          </thead>
          <tbody>
            {paged.length === 0 ? (
              <tr>
                <td colSpan="3" className="text-center text-muted">
                  No tasks match your filters.
                </td>
              </tr>
            ) : (
              paged.map((t) => (
                <tr key={t.t_id}>
                  <td>{t.t_title}</td>
                  <td>{t.category_name}</td>
                  <td className="text-center">
                    <input
                      type="checkbox"
                      checked={selectedTaskIds.includes(t.t_id)}
                      onChange={() => toggleTask(t.t_id)}
                      disabled={!PERMISSIONS.tasks.assign}
                    />
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
        <Pagination
          currentPage={page}
          totalItems={filtered.length}
          itemsPerPage={perPage}
          onPageChange={setPage}
        />
      </div>

      <div className="d-flex justify-content-end pt-3">
        {PERMISSIONS.tasks.assign && (
          <button
            className="btn btn-primary"
            onClick={handleSaveWithState}
            disabled={isSaving}
          >
            {isSaving ? "Saving..." : "Save Assignments"}
          </button>
        )}
      </div>

      <Modal show={showModal} onHide={() => setShowModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Edit Included Tasks</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {editingProgram ? (
            <>
              <p className="text-muted">
                Editing: <strong>{editingProgram.name}</strong>
              </p>
              <div className="mb-3">
                <label className="fw-semibold mb-2">
                  Select Employee Categories
                </label>
                <Select
                  isMulti
                  value={positionCategories
                    .filter((c) => editingCategoryIds.includes(c.c_id))
                    .map((c) => ({ value: c.c_id, label: c.c_name }))}
                  onChange={(selected) =>
                    setEditingCategoryIds((selected || []).map((s) => s.value))
                  }
                  options={positionCategories.map((c) => ({
                    value: c.c_id,
                    label: c.c_name,
                  }))}
                  placeholder="Search and select categories..."
                  className="basic-multi-select"
                  classNamePrefix="select"
                  styles={{
                    menu: (base) => ({ ...base, zIndex: 9999 }),
                  }}
                />
              </div>

              <div className="mb-3">
                <label className="fw-semibold mb-2">
                  Select Tasks to Include
                </label>
                <Select
                  isMulti
                  value={modalTasks
                    .filter((t) => editingTaskIds.includes(t.t_id))
                    .map((t) => ({ value: t.t_id, label: t.t_title }))}
                  onChange={(selected) =>
                    setEditingTaskIds((selected || []).map((s) => s.value))
                  }
                  options={modalTasks.map((t) => ({
                    value: t.t_id,
                    label: t.t_title,
                  }))}
                  placeholder="Search and select tasks..."
                  className="basic-multi-select"
                  classNamePrefix="select"
                  styles={{
                    menu: (base) => ({ ...base, zIndex: 9999 }),
                  }}
                />
              </div>
            </>
          ) : (
            <p>No program selected.</p>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={saveEditedTasks}>
            Save Changes
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={isSyncingPrograms} backdrop="static" keyboard={false} centered>
        <Modal.Header>
          <Modal.Title>Syncing Task Programs</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="mb-3">
            {syncJob?.message || "Updating user tasks from current program lists..."}
          </p>
          <div className="progress" style={{ height: 18 }}>
            <div
              className="progress-bar progress-bar-striped progress-bar-animated"
              role="progressbar"
              style={{ width: `${Math.max(1, Number(syncJob?.progress || 1))}%` }}
              aria-valuenow={Number(syncJob?.progress || 1)}
              aria-valuemin="0"
              aria-valuemax="100"
            >
              {Math.round(Number(syncJob?.progress || 1))}%
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="outline-danger"
            onClick={handleCancelSync}
            disabled={isCancelingSync || syncJob?.status === "canceling"}
          >
            {isCancelingSync || syncJob?.status === "canceling"
              ? "Canceling..."
              : "Cancel"}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default AssignTasksPage;
